import type { ComponentProps } from 'react';
import React, { useCallback, useState } from 'react';

import { formatWithMask, createNumberMask } from 'react-native-mask-input';

import { TextField } from '../../TextField';

const formatCurrency = (num: string | undefined) => {
  const dollarMask = createNumberMask({
    prefix: ['$'],
    delimiter: ',',
    separator: '.',
    precision: 2,
  });
  const { masked, unmasked } = formatWithMask({
    text: num,
    mask: dollarMask,
  });

  return { masked, unmasked };
};

export const TextFieldCurrency = (props: TextFieldCurrencyProps) => {
  const { label, onChangeText, defaultValue, ...restProps } = props;

  const [formattedValue, setFormattedValue] = useState(
    formatCurrency(defaultValue ?? '$').masked,
  );

  // ─── HELPERS ────────────────────────────────────────────────────────

  const handleChangeText = useCallback(
    (amt: string) => {
      const { masked } = formatCurrency(amt);

      setFormattedValue(masked);
      const tipInCents =
        Math.round(Number.parseFloat(masked.slice(1)) * 100) || 0;

      onChangeText?.(tipInCents);
    },
    [onChangeText],
  );

  // ────────────────────────────────────────────────────────────────────

  return (
    <TextField
      value={formattedValue}
      onChangeText={handleChangeText}
      maxLength={7}
      autoCapitalize="none"
      autoCorrect={false}
      keyboardType="numeric"
      label={label}
      {...restProps}
    />
  );
};

// ─── TYPES ──────────────────────────────────────────────────────────────────────

type TextFieldCurrencyProps = {
  readonly label: string;
  readonly onChangeText: (val: number) => void;
} & Omit<
  ComponentProps<typeof TextField>,
  | 'onChangeText'
  | 'autoCapitalize'
  | 'autoCorrect'
  | 'textContentType'
  | 'keyboardType'
>;
